import React, { useState, useEffect, useCallback } from "react";
import { getProfileWithElo, getUserProfileById } from "../services/profile";
import { ResumeData, PositionEnum } from "../services/parse";
import { Student, VoteCandidate } from "../types";
import EditProfileForm from "../components/EditProfileForm";
import {
  Box,
  Tabs,
  Tab,
  CircularProgress,
  Typography,
  Paper,
  Alert,
  Divider,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { hardDeleteUser } from "../services/user";
import { useAuth } from "../hooks/useAuth";
import { useParams } from "react-router-dom";

const Profile: React.FC = () => {
  const { userId } = useParams<{ userId: string; }>();
  const [user, setUser] = useState<Student | null>(null);
  const { session, signOut } = useAuth();
  const [tab, setTab] = useState(0);
  const [profileData, setProfileData] = useState<ResumeData | null>(null);
  const [elo, setElo] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!userId) {
        setError("User ID is missing.");
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        console.log(`[UserProfile] Fetching data for user ID: ${userId}`);
        const userData = await getUserProfileById(Number(userId));
        console.log("[UserProfile] User data received:", userData);
        setUser(userData);
        console.log("[UserProfile] User data set:", user);
      } catch (err) {
        console.error(
          `[UserProfile] Failed to fetch user data for ID ${userId}:`,
          err
        );
        const message =
          err instanceof Error ? err.message : "An unknown error occurred.";
        setError(`Failed to load user profile: ${message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [userId]);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setError(null);
    if (!session?.user?.email) {
      setError(
        "Could not load your user details. Please try logging out and in again."
      );
      setLoading(false);
      return;
    }
    try {
      const { profile, elo } = await getProfileWithElo(session.user.email);
      setProfileData(profile);
      setElo(elo);
    } catch (err) {
      setError("Could not load profile data.");
    } finally {
      setLoading(false);
    }
  }, [session?.user?.email]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  const getPositionDisplay = (pos?: PositionEnum | null) => {
    if (!pos) return "";
    switch (pos) {
      case PositionEnum.ACADEMIA:
        return "academic";
      case PositionEnum.DATA_SCIENCE:
        return "quant";
      case PositionEnum.ENGINEER:
        return "builder";
      default:
        return pos;
    }
  };

  const handleDeleteAccount = async () => {
    if (!session?.user?.id) {
      setDeleteError("User ID not found. Cannot delete account.");
      setDeleteConfirmationOpen(false);
      return;
    }
    setDeleteError(null);
    try {
      await hardDeleteUser(parseInt(session.user.id, 10));
      await signOut();
      window.location.href = "/";
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Unknown error during deletion.";
      setDeleteError(`Failed to delete account: ${msg}`);
    }
    setDeleteConfirmationOpen(false);
  };

  const toVoteCandidate = (data: ResumeData): VoteCandidate => ({
    id: session?.user?.id ? parseInt(session.user.id, 10) : 0,
    class_year: data.class_year,
    position: data.position,
    experiences: data.experience.map((exp, i) => ({
      id: i,
      user_id: 0,
      company: exp.company,
      title: exp.title,
      description: exp.description ?? "",
      company_url: exp.company_url ?? "",
      start_date: exp.start_date ?? "",
      end_date: exp.end_date ?? "",
      created_at: "",
      updated_at: "",
    })),
    projects: data.projects.map((proj, i) => ({
      id: i,
      user_id: 0,
      name: proj.name,
      description: proj.description ?? "",
      url: proj.url ?? "",
      created_at: "",
      updated_at: "",
    })),
    contests: data.contests.map((con, i) => ({
      id: i,
      user_id: 0,
      name: con.name,
      organizer: con.organizer ?? "",
      description: con.description ?? "",
      created_at: "",
      updated_at: "",
    })),
    misc: data.misc.map((m, i) => ({
      id: i,
      user_id: 0,
      key: m.key,
      value: m.value,
      created_at: "",
      updated_at: "",
    })),
    scores: data.scores.map((score, i) => ({
      id: score.id ?? i,
      user_id: score.user_id ?? 0,
      name: score.name ?? "",
      value: score.value ?? 0,
      verified: score.verified ?? false,
      created_at: score.created_at ?? "",
    })),
  });

  return (
    <Box
      sx={{
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: "0",
        height: "100%",
        bgcolor: "#f8f8f8",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          borderRadius: 0,
          mb: 0,
          borderBottom: "1px solid rgba(51, 51, 51, 0.2)",
          boxShadow: "none",
          backgroundColor: "#f8f8f8",
        }}
      >
        <Tabs
          value={tab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          sx={{
            "& .MuiTab-root": {
              color: "rgba(51, 51, 51, 0.7)",
              fontSize: "0.875rem",
              fontWeight: 300,
              letterSpacing: "0.025em",
              textTransform: "none",
              transition: "color 0.2s ease",
              "&.Mui-selected": {
                color: "#333333",
                fontWeight: 400,
              },
              "&:hover": {
                color: "#333333",
              },
            },
            "& .MuiTabs-indicator": {
              height: "2px",
              backgroundColor: "rgba(51, 51, 51, 0.6)",
            },
          }}
        >
          <Tab label="view profile" />
          <Tab label="edit profile" />
        </Tabs>
      </Paper>
      {loading ? (
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 300,
          }}
        >
          <CircularProgress sx={{ color: "rgba(51, 51, 51, 0.7)" }} />
        </Box>
      ) : error ? (
        <Alert
          severity="error"
          sx={{
            m: 2,
            borderRadius: "rounded-md",
            backgroundColor: "rgba(254, 226, 226, 0.5)",
            color: "#333333",
            fontWeight: 300,
          }}
        >
          {error}
        </Alert>
      ) : (
        <>
          {tab === 0 && profileData && (
            <Grid container sx={{ flexGrow: 1, minHeight: 0, height: "100%" }}>
              {/* Left: Basic Info */}
              <Grid
                size={{ xs: 12, md: 4 }}
                sx={{
                  borderRight: { md: "1px solid rgba(51, 51, 51, 0.2)" },
                  p: 3,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  minHeight: 0,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 300,
                    mb: 1,
                    color: "#333333",
                    letterSpacing: "0.025em",
                    fontSize: { xs: "1.5rem", md: "1.25rem" },
                  }}
                >
                  basic information
                </Typography>
                <Divider sx={{ mb: 1, borderColor: "rgba(51, 51, 51, 0.2)" }} />

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 300,
                        color: "rgba(51, 51, 51, 0.7)",
                        mb: 0.5,
                      }}
                    >
                      name
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 300, color: "#333333" }}
                    >
                      {profileData.name}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 300,
                        color: "rgba(51, 51, 51, 0.7)",
                        mb: 0.5,
                      }}
                    >
                      email
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 300, color: "#333333" }}
                    >
                      {profileData.email}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 300,
                        color: "rgba(51, 51, 51, 0.7)",
                        mb: 0.5,
                      }}
                    >
                      class year
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 300, color: "#333333" }}
                    >
                      {profileData.class_year}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 300,
                        color: "rgba(51, 51, 51, 0.7)",
                        mb: 0.5,
                      }}
                    >
                      position
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 300, color: "#333333" }}
                    >
                      {getPositionDisplay(profileData.position)}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 300,
                        color: "rgba(51, 51, 51, 0.7)",
                        mb: 0.5,
                      }}
                    >
                      elo
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 300, color: "#333333" }}
                    >
                      {elo ?? "n/a"}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mt: "auto", pt: 3 }}>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => setDeleteConfirmationOpen(true)}
                    sx={{
                      width: "100%",
                      fontWeight: 300,
                      fontSize: "0.875rem",
                      border: "1px solid rgba(211, 47, 47, 0.5)",
                      borderRadius: "4px",
                      textTransform: "none",
                      py: 1,
                      "&:hover": {
                        border: "1px solid rgba(211, 47, 47, 0.8)",
                        backgroundColor: "rgba(211, 47, 47, 0.04)",
                      },
                    }}
                  >
                    delete account
                  </Button>
                </Box>
              </Grid>
              {/* Right: Stats */}
              <Grid
                size={{ xs: 12, md: 8 }}
                sx={{
                  p: 3,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                {/* TODO: add something here... medals, etc. */}
              </Grid>
            </Grid>
          )}
          {tab === 1 && profileData && session?.user && (
            <Box
              sx={{
                flexGrow: 1,
                minHeight: 0,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                p: 0,
              }}
            >
              <EditProfileForm
                userProfile={{
                  id: session.user.id,
                  email: session.user.email,
                  name: profileData.name || undefined,
                }}
                initialProfileData={profileData}
                onSaveSuccess={fetchProfile}
              />
            </Box>
          )}
        </>
      )}

      <Dialog
        open={deleteConfirmationOpen}
        onClose={() => setDeleteConfirmationOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: "4px",
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
            backgroundColor: "#fff",
          },
        }}
      >
        <DialogTitle
          id="alert-dialog-title"
          sx={{
            fontWeight: 300,
            letterSpacing: "0.025em",
            color: "#333333",
            pt: 3,
          }}
        >
          {"confirm account deletion"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="alert-dialog-description"
            sx={{
              fontWeight: 300,
              color: "rgba(51, 51, 51, 0.7)",
            }}
          >
            Are you sure you want to permanently delete your account? This
            action cannot be undone.
          </DialogContentText>
          {deleteError && (
            <Alert
              severity="error"
              sx={{
                mt: 2,
                borderRadius: "4px",
                backgroundColor: "rgba(254, 226, 226, 0.5)",
                color: "#333333",
                fontWeight: 300,
              }}
            >
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setDeleteConfirmationOpen(false)}
            sx={{
              fontWeight: 300,
              fontSize: "0.875rem",
              border: "1px solid rgba(51, 51, 51, 0.2)",
              borderRadius: "4px",
              color: "rgba(51, 51, 51, 0.7)",
              textTransform: "none",
              px: 2,
              "&:hover": {
                border: "1px solid rgba(51, 51, 51, 0.6)",
                backgroundColor: "transparent",
              },
            }}
          >
            cancel
          </Button>
          <Button
            onClick={handleDeleteAccount}
            color="error"
            autoFocus
            sx={{
              fontWeight: 300,
              fontSize: "0.875rem",
              textTransform: "none",
              px: 2,
              "&:hover": {
                backgroundColor: "rgba(211, 47, 47, 0.04)",
              },
            }}
          >
            delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Profile;
